import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { Skeleton } from "../../Components/Needed";
import { EmptyState } from "../../Components/Kit";
import { diagnosePerson, downloadText, exportDeliveries, fetchDelivery, fetchDeliveries, resendDelivery, retryDelivery } from "../../Helpers/notificationConsoleApi";
import { AUDIENCE_LABEL, STATUS_CLASS, eventName, whenText } from "./shared";

// Notification Centre > Delivery (R6.5): the delivery log and "Why not received?" as one search by person.
// Find a person: their devices, their own settings and their last messages are checked, and the log below
// shows only their messages. Without a person, the log shows everything, with its filters.
const STATUS_OPTIONS = ["queued", "sending", "sent", "delivered", "read", "failed", "skipped"];
const EMPTY = { status: "", channel: "", event: "", q: "", from: "", to: "" };
const LEVEL_CLASS = { problem: "nc-find-problem", info: "nc-find-info", ok: "nc-find-ok" };

const clean = (filters) => Object.fromEntries(Object.entries(filters).filter(([, value]) => String(value || "").trim() !== ""));

const StatusBadge = ({ status }) => <span className={`nc-status ${STATUS_CLASS[status] || "bg-secondary-subtle text-secondary"}`}>{status}</span>;

// Side panel with everything about one delivery, and the retry / resend buttons.
const DetailPanel = ({ deliveryId, onClose, onChanged, onWhy }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState("");

    const load = useCallback(async () => {
        const result = await fetchDelivery(deliveryId);
        if (result.ok) setDetail(result.data);
        else toast.error(result.message);
        setLoading(false);
    }, [deliveryId]);

    useEffect(() => { setLoading(true); load(); }, [load]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const act = async (name, run, doneMessage) => {
        setWorking(name);
        const result = await run(deliveryId);
        setWorking("");
        if (!result.ok) return toast.error(result.message);
        toast.success(doneMessage);
        onChanged();
        return load();
    };

    const item = detail?.item;

    return createPortal(
        <div className="nc-drawer-backdrop" onClick={onClose}>
            <aside className="nc-drawer" role="dialog" aria-modal="true" aria-label="Delivery details" onClick={(e) => e.stopPropagation()}>
                <div className="nc-drawer-head">
                    <h6 className="mb-0 fw-semibold">Delivery details</h6>
                    <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
                </div>
                <div className="nc-drawer-body">
                    {loading || !item ? (
                        <><Skeleton height={20} width="50%" className="mb-3" /><Skeleton height={80} className="mb-3" /><Skeleton height={60} /></>
                    ) : (
                        <>
                            <div className="mb-3"><StatusBadge status={item.status} /> <span className="nc-sub ms-1">{item.channel}</span></div>
                            <p className="nc-reason">{item.reason}</p>

                            <div className="nc-block">
                                <div className="fw-semibold">{item.title}</div>
                                <div className="small">{item.body}</div>
                            </div>

                            <dl className="nc-facts">
                                <dt>Message</dt><dd>{eventName(item.event)}</dd>
                                <dt>To</dt><dd>{AUDIENCE_LABEL[item.recipientType]} {item.recipientId}</dd>
                                {item.bookingId ? <><dt>Booking</dt><dd>{item.bookingId}</dd></> : null}
                                {item.taskId ? <><dt>Task</dt><dd>{item.taskId}</dd></> : null}
                                <dt>Created</dt><dd>{whenText(item.createdAt)}</dd>
                                <dt>Sent</dt><dd>{whenText(item.sentAt)}</dd>
                                <dt>Attempts</dt><dd>{item.attempts}</dd>
                                {item.nextAttemptAt && item.status === "queued" ? <><dt>Next try</dt><dd>{whenText(item.nextAttemptAt)}</dd></> : null}
                                {item.error ? <><dt>Error</dt><dd className="text-danger">{item.error.code}{item.error.message ? ` - ${item.error.message}` : ""}</dd></> : null}
                                <dt>Delivery id</dt><dd className="nc-mono">{item.deliveryId}</dd>
                            </dl>

                            <div className="fw-semibold small mb-1">Devices ({detail.devices.length})</div>
                            {detail.devices.length === 0 ? (
                                <div className="nc-sub mb-3">No device registered.</div>
                            ) : (
                                <ul className="nc-devices">
                                    {detail.devices.map((device, index) => (
                                        <li key={`${device.platform}-${index}`}>
                                            {device.label || device.platform || "Device"} - {device.active ? "active" : "inactive"}{device.lastSeenAt ? `, last seen ${whenText(device.lastSeenAt)}` : ""}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <div className="nc-actions">
                                {item.canRetry && <button type="button" className="btn btn-primary btn-sm" disabled={!!working} onClick={() => act("retry", retryDelivery, "Queued again")}>{working === "retry" ? "Queuing..." : "Retry now"}</button>}
                                <button type="button" className="btn btn-outline-primary btn-sm" disabled={!!working} onClick={() => act("resend", resendDelivery, "Sent a fresh copy")}>{working === "resend" ? "Sending..." : "Resend as new"}</button>
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { onClose(); onWhy(item.recipientType, item.recipientId); }}>Why not received?</button>
                            </div>
                            <div className="nc-sub mt-2">Retry puts this same message back in the queue. Resend creates a new copy, even if the original was dropped on purpose.</div>
                        </>
                    )}
                </div>
            </aside>
        </div>,
        document.body
    );
};


// One person's check: what we found, their devices, their last messages.
const PersonReport = ({ report, recipientType, onClear }) => (
    <section className="nc-person" aria-label="Why not received?">
        <div className="nc-person-head">
            <div>
                <div className="fw-bold">{report.person.name || "No name on file"}</div>
                <div className="nc-sub">{AUDIENCE_LABEL[recipientType]} {report.person.id}{report.person.phone ? ` · ${report.person.phone}` : ""}</div>
            </div>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClear}>Show everyone</button>
        </div>
        <div className="nc-findings">
            {report.findings.map((finding, index) => <div key={index} className={`nc-find ${LEVEL_CLASS[finding.level] || "nc-find-info"}`}>{finding.text}</div>)}
        </div>
        <div className="nc-person-grid">
            <div>
                <div className="fw-semibold small mb-1">Devices ({report.devices.length})</div>
                {report.devices.length === 0 ? <div className="nc-sub">None registered.</div> : (
                    <ul className="nc-devices mb-0">
                        {report.devices.map((device, index) => <li key={index}>{device.label || device.platform || "Device"} - {device.active ? "active" : "inactive"}{device.lastSeenAt ? `, last seen ${whenText(device.lastSeenAt)}` : ""}</li>)}
                    </ul>
                )}
            </div>
            <div>
                <div className="fw-semibold small mb-1">Last messages</div>
                {report.recent.length === 0 ? <div className="nc-sub">No messages yet.</div> : (
                    <div className="nc-recent">
                        {report.recent.slice(0, 5).map((note) => (
                            <div className="nc-recent-item" key={note.notificationId}>
                                <div className="d-flex justify-content-between gap-2">
                                    <div className="fw-semibold small">{note.title || eventName(note.event)}</div>
                                    <div className="nc-sub text-nowrap">{whenText(note.createdAt)}</div>
                                </div>
                                {note.deliveries.length === 0 ? <div className="nc-sub">Inbox only (nothing was queued).</div> : note.deliveries.map((delivery) => (
                                    <div className="nc-recent-delivery" key={delivery.deliveryId}>
                                        <span className={`nc-status ${STATUS_CLASS[delivery.status] || "bg-secondary-subtle text-secondary"}`}>{delivery.status}</span>
                                        <span className="small">{delivery.channel}: {delivery.reason}</span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    </section>
);

const DeliverySection = ({ carry }) => {
    const startFilters = { ...EMPTY, ...(carry?.filters || {}) };
    const [who, setWho] = useState(carry?.recipientType || carry?.filters?.recipientType || "customer");
    const [personQ, setPersonQ] = useState(carry?.q || "");
    const [checking, setChecking] = useState(false);
    const [person, setPerson] = useState(null); // { recipientType, report }
    const [filters, setFilters] = useState(startFilters);
    const [applied, setApplied] = useState(startFilters);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [more, setMore] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [openId, setOpenId] = useState("");
    const [showFilters, setShowFilters] = useState(Object.values(clean(startFilters)).length > 0);
    const requestRef = useRef(0);

    // The log's query: the filters, narrowed to the person when one is found.
    const query = useCallback((extra = {}) => ({ ...clean(applied), ...(person ? { recipientType: person.recipientType, recipientId: person.report.person.id } : {}), limit: 30, ...extra }), [applied, person]);

    const load = useCallback(async () => {
        const request = ++requestRef.current;
        setLoading(true);
        const result = await fetchDeliveries(query());
        if (request !== requestRef.current) return;
        if (result.ok) {
            setItems(result.data.items || []);
            setMore(result.data.hasMore ? result.data.nextBefore : null);
        } else toast.error(result.message);
        setLoading(false);
    }, [query]);
    useEffect(() => { load(); }, [load]);

    const check = useCallback(async (type, text) => {
        if (!String(text || "").trim()) { toast.error("Enter an id or phone number"); return; }
        setChecking(true);
        const result = await diagnosePerson({ recipientType: type, q: String(text).trim() });
        setChecking(false);
        if (!result.ok) { setPerson(null); toast.error(result.message); return; }
        setPerson({ recipientType: type, report: result.data });
    }, []);
    useEffect(() => { if (carry?.q) check(carry.recipientType || "customer", carry.q); }, [carry, check]);

    const loadMore = async () => {
        setLoadingMore(true);
        const result = await fetchDeliveries(query({ before: more }));
        setLoadingMore(false);
        if (!result.ok) return toast.error(result.message);
        setItems((prev) => [...prev, ...(result.data.items || [])]);
        return setMore(result.data.hasMore ? result.data.nextBefore : null);
    };
    const setField = (name) => (e) => setFilters((prev) => ({ ...prev, [name]: e.target.value }));
    const exportCsv = async () => {
        setExporting(true);
        const params = query();
        delete params.limit;
        const result = await exportDeliveries(params);
        setExporting(false);
        if (!result.ok) return toast.error(result.message);
        downloadText(result.data.filename, result.data.csv);
        return toast.success(`Saved ${result.data.rows} rows${result.data.capped ? " (the most recent only - narrow the dates for the rest)" : ""}`);
    };
    const why = (type, id) => { setWho(type); setPersonQ(id); check(type, id); window.scrollTo({ top: 0, behavior: "smooth" }); };

    return (
        <div className="nc-delivery">
            <form className="nc-find-person" onSubmit={(e) => { e.preventDefault(); check(who, personQ); }}>
                <div className="nc-find-person-label">
                    <div className="fw-bold">Find a person</div>
                    <div className="nc-sub">Customer id or phone, or a staff / admin id. We check their devices, their own settings and their last messages, and show only their deliveries below.</div>
                </div>
                <div className="nc-find-person-row">
                    <select className="form-select form-select-sm" value={who} onChange={(e) => setWho(e.target.value)} aria-label="Person type">
                        {Object.entries(AUDIENCE_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                    <input className="form-control form-control-sm nc-search" placeholder={who === "customer" ? "Customer id or phone" : "Id"} value={personQ} onChange={(e) => setPersonQ(e.target.value)} aria-label="Id or phone" />
                    <button type="submit" className="btn btn-primary btn-sm" disabled={checking}>{checking ? "Checking..." : "Check"}</button>
                </div>
            </form>

            {person ? <PersonReport report={person.report} recipientType={person.recipientType} onClear={() => { setPerson(null); setPersonQ(""); }} /> : null}

            <section className="settings-card nc-log" aria-label="Delivery log">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <h2 className="nc-ov-h mb-0">{person ? `Deliveries to ${person.report.person.name || person.report.person.id}` : "All deliveries"}</h2>
                    <div className="d-flex flex-wrap gap-2">
                        <button type="button" className="btn btn-outline-secondary btn-sm" aria-expanded={showFilters} onClick={() => setShowFilters(!showFilters)}>{showFilters ? "Hide filters" : "Filters"}{Object.keys(clean(applied)).length ? ` (${Object.keys(clean(applied)).length})` : ""}</button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={exportCsv} disabled={exporting}>{exporting ? "Preparing..." : "Download CSV"}</button>
                    </div>
                </div>
                {showFilters ? (
                    <form className="nc-filters mt-3" onSubmit={(e) => { e.preventDefault(); setApplied({ ...filters }); }}>
                        <input className="form-control form-control-sm nc-search" placeholder="Booking, task or message id" value={filters.q} onChange={setField("q")} aria-label="Search" />
                        <select className="form-select form-select-sm" value={filters.status} onChange={setField("status")} aria-label="Status">
                            <option value="">Any status</option>
                            {STATUS_OPTIONS.map((value) => <option key={value} value={value}>{value}</option>)}
                        </select>
                        <select className="form-select form-select-sm" value={filters.channel} onChange={setField("channel")} aria-label="Channel">
                            <option value="">Any channel</option>
                            <option value="push">Push</option>
                            <option value="whatsapp">WhatsApp</option>
                            <option value="email">Email</option>
                            <option value="sms">SMS</option>
                        </select>
                        <input className="form-control form-control-sm" placeholder="Event, e.g. booking.confirmed" value={filters.event} onChange={setField("event")} aria-label="Event" />
                        <input type="date" className="form-control form-control-sm" value={filters.from} onChange={setField("from")} aria-label="From date" />
                        <input type="date" className="form-control form-control-sm" value={filters.to} onChange={setField("to")} aria-label="To date" />
                        <button type="submit" className="btn btn-primary btn-sm">Apply</button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setFilters(EMPTY); setApplied(EMPTY); }}>Clear</button>
                    </form>
                ) : null}

                {loading ? (
                    <div className="mt-3"><Skeleton height={40} className="mb-2" /><Skeleton height={40} className="mb-2" /><Skeleton height={40} /></div>
                ) : items.length === 0 ? (
                    <div className="mt-3"><EmptyState title="Nothing found" text={person ? "No messages to this person match." : "No deliveries match these filters."} /></div>
                ) : (
                    <>
                        <div className="nc-table-wrap mt-3">
                            <table className="table table-sm align-middle nc-table nc-clickable">
                                <thead><tr><th>When</th><th>Message</th><th>To</th><th>Channel</th><th>Status</th></tr></thead>
                                <tbody>
                                    {items.map((row) => (
                                        <tr key={row.deliveryId} onClick={() => setOpenId(row.deliveryId)} tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") setOpenId(row.deliveryId); }}>
                                            <td className="text-nowrap">{whenText(row.createdAt)}</td>
                                            <td>
                                                <div className="fw-semibold">{row.title || eventName(row.event)}</div>
                                                <div className="nc-sub">{eventName(row.event)}{row.bookingId ? ` - ${row.bookingId}` : ""}</div>
                                            </td>
                                            <td className="text-nowrap">{AUDIENCE_LABEL[row.recipientType]}<div className="nc-sub nc-mono">{row.recipientId}</div></td>
                                            <td>{row.channel}</td>
                                            <td><StatusBadge status={row.status} />{row.error ? <div className="nc-sub">{row.error.code}</div> : null}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {more ? <div className="text-center mt-2"><button type="button" className="btn btn-outline-secondary btn-sm" onClick={loadMore} disabled={loadingMore}>{loadingMore ? "Loading..." : "Show older"}</button></div> : null}
                    </>
                )}
            </section>

            {openId ? <DetailPanel deliveryId={openId} onClose={() => setOpenId("")} onChanged={load} onWhy={why} /> : null}
        </div>
    );
};

export default DeliverySection;
